import {ReachSegment, SegmentCategory} from "../../apis/requests/segments/types";

type BlockSegment = Pick<ReachSegment, "id" | "segment_category">;

type CategorizedSegment = BlockSegment & Pick<ReachSegment, "is_category_fallback">;

export type CategoryBlockPosition = {isFirst: boolean; isLast: boolean};

export const CATEGORY_BLOCK_SPACING = 8;

export const compareFallbackLast = (
  a: Pick<ReachSegment, "is_category_fallback">,
  b: Pick<ReachSegment, "is_category_fallback">
) => Number(a.is_category_fallback) - Number(b.is_category_fallback);

export const blockKeyOf = (segment: BlockSegment): string => segment.segment_category ?? `segment-${segment.id}`;

const indexesOfBlock = (blockKeys: (string | undefined)[], key: string) =>
  blockKeys.flatMap((blockKey, index) => (blockKey === key ? [index] : []));

export const keepCategoriesTogether = <T extends CategorizedSegment>(segments: T[]): T[] => {
  const placedCategories = new Set<SegmentCategory>();
  return segments.flatMap((segment) => {
    const category = segment.segment_category;
    if (!category) return [segment];
    if (placedCategories.has(category)) return [];
    placedCategories.add(category);
    return segments.filter((member) => member.segment_category === category).sort(compareFallbackLast);
  });
};

export const moveBlock = <T extends BlockSegment>(segments: T[], activeId: number, overId: number): T[] => {
  const active = segments.find((segment) => segment.id === activeId);
  const over = segments.find((segment) => segment.id === overId);
  if (!active || !over) return segments;
  const activeKey = blockKeyOf(active);
  const overKey = blockKeyOf(over);
  if (activeKey === overKey) return segments;
  const movingDown = segments.indexOf(active) < segments.indexOf(over);
  const moving = segments.filter((segment) => blockKeyOf(segment) === activeKey);
  const remaining = segments.filter((segment) => blockKeyOf(segment) !== activeKey);
  const overIndexes = indexesOfBlock(remaining.map(blockKeyOf), overKey);
  const insertAt = movingDown ? overIndexes[overIndexes.length - 1] + 1 : overIndexes[0];
  return [...remaining.slice(0, insertAt), ...moving, ...remaining.slice(insertAt)];
};

export const rankByBlock = <T extends BlockSegment>(segments: T[]): Map<number, number> => {
  const rankByKey = new Map<string, number>();
  return new Map(
    segments.map((segment) => {
      const key = blockKeyOf(segment);
      if (!rankByKey.has(key)) rankByKey.set(key, rankByKey.size + 1);
      return [segment.id, rankByKey.get(key)!] as const;
    })
  );
};

export const categoryBlockPositions = <T extends BlockSegment>(segments: T[]): Map<number, CategoryBlockPosition> => {
  const positions = new Map<number, CategoryBlockPosition>();
  segments.forEach((segment, index) => {
    if (!segment.segment_category) return;
    positions.set(segment.id, {
      isFirst: segments[index - 1]?.segment_category !== segment.segment_category,
      isLast: segments[index + 1]?.segment_category !== segment.segment_category,
    });
  });
  return positions;
};

type RowRect = {top: number; height: number};

export const blockDisplacement = ({
  blockKeys,
  rects,
  activeIndex,
  overIndex,
  index,
}: {
  blockKeys: (string | undefined)[];
  rects: RowRect[];
  activeIndex: number;
  overIndex: number;
  index: number;
}): number => {
  const activeKey = blockKeys[activeIndex];
  const overKey = blockKeys[overIndex];
  if (activeKey === undefined || overKey === undefined || activeKey === overKey) return 0;
  const activeIndexes = indexesOfBlock(blockKeys, activeKey);
  const overIndexes = indexesOfBlock(blockKeys, overKey);
  const firstActiveIndex = activeIndexes[0];
  const lastActiveIndex = activeIndexes[activeIndexes.length - 1];
  const firstRect = rects[firstActiveIndex];
  const lastRect = rects[lastActiveIndex];
  if (!firstRect || !lastRect) return 0;
  const blockHeight = lastRect.top + lastRect.height - firstRect.top;
  if (overIndex > activeIndex)
    return index > lastActiveIndex && index <= overIndexes[overIndexes.length - 1] ? -blockHeight : 0;
  return index >= overIndexes[0] && index < firstActiveIndex ? blockHeight : 0;
};
